import React, { useEffect, useId, useRef, useState } from 'react';
import { IconShare } from '@tabler/icons-react';
import ShareButton from '../SocialShareButton/SocialShareButton.jsx';
import buttonStyles from '../SocialShareButton/SocialShareButton.module.scss';
import { canUseNativeShare, prefetchShareFile, shareImage } from '../shared/nativeShare.js';
import styles from './SocialShareBar.module.scss';

const DEFAULT_SERVICES = [
    'whatsApp',
    'facebook',
    'email',
    'telegram',
    'facebookMessenger',
    'xing',
    'linkedin',
    'pinterest',
    'bluesky',
    'instagram',
];

const VISIBLE_SERVICE_COUNT = {
    iconbuttonsKompakt: 3,
    iconbuttonsVoll: 7,
};

// `flat` ist der volle Button mit Label (Cassiopeia-Default), die beiden
// `iconbuttons*`-Varianten sind das Icon-Raster (Bigfoot-Default).
const SUPPORTED_DISPLAY_MODES = ['dropdown', 'flat', 'iconbuttonsVoll', 'iconbuttonsKompakt'];

// Each icon button pulses for 1.8s (see .attentionPulse); the step adds a short pause before the next one starts.
const PULSE_STEP_MS = 2400;

/**
 * Returns the index of the button that currently carries the attention pulse and keeps cycling through all of them.
 * A count below two disables the rotation, as there is nothing to rotate through.
 */
const useRotatingPulse = (count) => {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setActiveIndex(0);
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (count < 2 || reducedMotion) return undefined;

        const timer = setInterval(() => setActiveIndex((index) => (index + 1) % count), PULSE_STEP_MS);
        return () => clearInterval(timer);
    }, [count]);

    return count > 0 ? activeIndex % count : -1;
};

// Older pages still store the retired variants; keep them rendering with the closest current design.
// `flat` steht bewusst nicht mehr hier: es ist ein eigener, unterscheidbarer Modus.
const LEGACY_DISPLAY_MODES = {
    bar: 'iconbuttonsKompakt',
    alt: 'dropdown',
};

export const normalizeDisplayMode = (displayMode) => {
    const resolved = LEGACY_DISPLAY_MODES[displayMode] || displayMode;
    return SUPPORTED_DISPLAY_MODES.includes(resolved) ? resolved : 'iconbuttonsVoll';
};

const MoreToggleContent = () => (
    <>
        <span className={buttonStyles.IconCircle}>
            <IconShare size={24} aria-hidden="true" />
        </span>
        <span className={buttonStyles.IconLabel}>Mehr</span>
    </>
);

/**
 * Replaces the former "more services" panel: opens the native share sheet for the page.
 * The configured graphic is deliberately not attached here, it is reserved for Instagram.
 */
const MoreShareButton = ({ className, title, text, url }) => (
    <button type="button" className={className} onClick={() => shareImage({ title, text, url })}>
        <MoreToggleContent />
    </button>
);

/**
 * Disclosure button that reveals additional share services in a two-column panel.
 * Uses a disclosure pattern instead of a menu role so the links stay in the natural tab order.
 */
const ShareDisclosure = ({
    services,
    shareProps,
    toggleClassName,
    toggleContent,
    panelClassName,
    panelLabel,
    wrapperClassName,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const openedWithKeyboard = useRef(false);
    const wrapperRef = useRef(null);
    const panelRef = useRef(null);
    const toggleRef = useRef(null);
    const panelId = useId();

    useEffect(() => {
        if (!isOpen) return undefined;

        // Moving focus into the panel on a mouse click would leave the browser's focus ring
        // on the first entry, so it only happens when the panel was opened from the keyboard.
        if (openedWithKeyboard.current) panelRef.current?.querySelector('a, button')?.focus();

        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                toggleRef.current?.focus();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    return (
        <div className={`${styles.MenuWrapper} ${wrapperClassName || ''}`.trim()} ref={wrapperRef}>
            <button
                ref={toggleRef}
                type="button"
                className={toggleClassName}
                aria-expanded={isOpen}
                aria-controls={isOpen ? panelId : undefined}
                onClick={(event) => {
                    // Keyboard activation reports detail 0, pointer activation reports the click count.
                    openedWithKeyboard.current = event.detail === 0;
                    setIsOpen((open) => !open);
                }}
            >
                {toggleContent}
            </button>
            {isOpen && (
                <div
                    id={panelId}
                    ref={panelRef}
                    className={`${styles.MenuPanel} ${panelClassName || ''}`.trim()}
                    aria-label={panelLabel}
                >
                    {services.map((service) => (
                        <ShareButton key={service} shareType={service} variant="menu" {...shareProps} />
                    ))}
                </div>
            )}
        </div>
    );
};

const SocialShareBar = ({
    headline,
    shareText,
    shareTitle,
    mailSubject,
    mailBody,
    url,
    // Graphic handed to the native share sheet (Instagram and the "Mehr" button).
    shareImageUrl,
    // Optionaler Callback `(shareType) => void`, wird bei jedem Teilen-Button ausgelöst
    // (Cassiopeia bucht darüber den Achievement-PATCH). Der native Share-Sheet-Button
    // kennt keinen Dienst und bleibt aussen vor.
    onShare,
    // Left for the variants to resolve: the dropdown sits below the button, so it aligns left by default.
    alignment,
    displayMode = 'iconbuttonsVoll',
    services = DEFAULT_SERVICES,
    dropdownLabel = 'Mehr Stimmen sammeln? Jetzt teilen!',
    // The project page uses the dropdown variant as a full-width call to action.
    fullWidth = false,
    style = {},
}) => {
    const activeServices = Array.isArray(services) && services.length > 0 ? services : DEFAULT_SERVICES;
    const shareProps = { shareTitle, shareText, shareUrl: url, mailSubject, mailBody, shareImageUrl, onShare };
    const mode = normalizeDisplayMode(displayMode);

    // Fetched up front so the click handler can call navigator.share() while the activation is alive.
    useEffect(() => {
        prefetchShareFile(shareImageUrl);
    }, [shareImageUrl]);

    const visibleCount = VISIBLE_SERVICE_COUNT[mode] ?? 0;
    const overflowCount = visibleCount ? Math.max(activeServices.length - visibleCount, 0) : 0;
    const pulseCount = visibleCount ? Math.min(activeServices.length, visibleCount) + (overflowCount > 0 ? 1 : 0) : 0;
    const pulseIndex = useRotatingPulse(pulseCount);

    const resolvedAlignment = alignment || (mode === 'dropdown' ? 'left' : 'center');
    const alignClass =
        resolvedAlignment === 'left' ? 'text-start' : resolvedAlignment === 'right' ? 'text-end' : 'text-center';
    const justifyClass =
        resolvedAlignment === 'left'
            ? 'justify-content-start'
            : resolvedAlignment === 'right'
              ? 'justify-content-end'
              : 'justify-content-center';

    const renderHeadline = () => headline && <h4 className={`mb-3 ${alignClass}`}>{headline}</h4>;

    // „Flach": ein voll breiter Button mit Label pro Dienst. Das Markup entspricht
    // 1:1 dem bisherigen Cassiopeia-Rendering (Row/Col-Utilities statt eigener Klassen).
    if (mode === 'flat') {
        return (
            <div className={`social-share-flat-wrapper w-100 ${alignClass}`} style={style}>
                {headline && <h4 className="social-share-headline mb-3">{headline}</h4>}
                <div className={`row g-2 ${justifyClass}`}>
                    {activeServices.map((service) => (
                        <div key={service} className="col-6 col-sm-6 col-md-auto flex-grow-1 flex-md-grow-0">
                            <ShareButton shareType={service} variant="flat" {...shareProps} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (mode === 'iconbuttonsVoll' || mode === 'iconbuttonsKompakt') {
        const visibleServices = activeServices.slice(0, visibleCount);
        const overflowServices = activeServices.slice(visibleCount);
        const gridClass = mode === 'iconbuttonsKompakt' ? styles.IconRow : styles.IconGrid;
        const moreToggleClassName = `${buttonStyles.IconButton} ${buttonStyles.Service_more} ${
            pulseIndex === visibleServices.length ? buttonStyles.attentionPulse : ''
        }`.trim();
        // Without a share sheet the button keeps listing the remaining services instead.
        const moreButton = canUseNativeShare() ? (
            <MoreShareButton
                className={moreToggleClassName}
                title={shareTitle}
                text={shareText || 'Jetzt für uns abstimmen!'}
                url={url || (typeof window !== 'undefined' ? window.location.href : undefined)}
            />
        ) : (
            <ShareDisclosure
                services={overflowServices}
                shareProps={shareProps}
                panelLabel="Weitere Dienste"
                toggleClassName={moreToggleClassName}
                toggleContent={<MoreToggleContent />}
            />
        );

        return (
            <div className={styles.SocialShareBar} style={style}>
                {renderHeadline()}
                <div className={gridClass}>
                    {visibleServices.map((service, index) => (
                        <ShareButton
                            key={service}
                            shareType={service}
                            variant="icon"
                            pulse={pulseIndex === index}
                            {...shareProps}
                        />
                    ))}
                    {overflowServices.length > 0 && moreButton}
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.DropdownWrapper} ${alignClass}`} style={style}>
            {renderHeadline()}
            <ShareDisclosure
                services={activeServices}
                shareProps={shareProps}
                panelLabel="Dienste zum Teilen"
                panelClassName={styles.DropdownPanel}
                wrapperClassName={fullWidth ? styles.MenuWrapperFull : styles.MenuWrapperNarrowFull}
                toggleClassName={`btn btn-outline-primary ${styles.DropdownToggle}`}
                toggleContent={
                    <>
                        {dropdownLabel}
                        <IconShare className="ps-1" size={20} aria-hidden="true" />
                    </>
                }
            />
        </div>
    );
};

export default SocialShareBar;
